import {
  getViewportForBounds,
  type FitViewOptions,
  type Rect,
  type Viewport,
  type XYPosition,
} from '@xyflow/react';

interface DiagramSize {
  width: number;
  height: number;
}

interface ReadableViewportParams {
  nodePositions: ReadonlyArray<XYPosition>;
  nodeSize: Readonly<DiagramSize>;
  canvasSize: Readonly<DiagramSize>;
  padding: FitViewOptions['padding'];
  minZoom: number;
  // Everything the chart draws, when it reaches past the nodes.
  contentBounds?: Rect;
}

class ReadableViewportHelper {
  static readonly ORG_MIN_ZOOM = 0.6;

  static readonly TAX_MIN_ZOOM = 0.5;

  static readonly MANUAL_MIN_ZOOM = 0.25;

  static readonly MAX_ZOOM = 1;

  static readonly DEFAULT_PADDING = 0.15;

  // Gap between an overflowing chart's edge and the canvas edge, clear of the
  // floating top-left/top-right chrome.
  static readonly OVERFLOW_INSET = 80;

  // Fits the chart when it is readable at that zoom. Otherwise holds minZoom
  // and, on each overflowing axis, anchors to the top and centres the top rank
  // (the ultimate owners) so the user scrolls to reach the rest.
  static getViewport({
    nodePositions,
    nodeSize,
    canvasSize,
    padding,
    minZoom,
    contentBounds,
  }: Readonly<ReadableViewportParams>): Viewport | null {
    if (
      nodePositions.length === 0 ||
      canvasSize.width <= 0 ||
      canvasSize.height <= 0
    )
      return null;
    const bounds =
      contentBounds ??
      ReadableViewportHelper.getBounds(nodePositions, nodeSize);
    const fitted = getViewportForBounds(
      bounds,
      canvasSize.width,
      canvasSize.height,
      minZoom,
      ReadableViewportHelper.MAX_ZOOM,
      padding ?? ReadableViewportHelper.DEFAULT_PADDING
    );
    const { zoom } = fitted;
    const inset = ReadableViewportHelper.OVERFLOW_INSET;
    const overflowsX = bounds.width * zoom + 2 * inset > canvasSize.width;
    const overflowsY = bounds.height * zoom + 2 * inset > canvasSize.height;
    if (zoom > minZoom || (!overflowsX && !overflowsY)) return fitted;

    return {
      x: overflowsX
        ? ReadableViewportHelper.getTopRankCenteredX(
            nodePositions,
            nodeSize,
            bounds,
            canvasSize.width,
            zoom
          )
        : fitted.x,
      y: overflowsY ? inset - bounds.y * zoom : fitted.y,
      zoom,
    };
  }

  static getZoomRange({
    nodePositions,
    nodeSize,
    canvasSize,
    padding,
    minZoom,
    contentBounds,
  }: Readonly<ReadableViewportParams>): { minZoom: number; maxZoom: number } {
    const maxZoom = ReadableViewportHelper.MAX_ZOOM;
    if (
      nodePositions.length === 0 ||
      canvasSize.width <= 0 ||
      canvasSize.height <= 0
    )
      return { minZoom, maxZoom };
    const bounds =
      contentBounds ??
      ReadableViewportHelper.getBounds(nodePositions, nodeSize);
    const { zoom: zoomToFitAll } = getViewportForBounds(
      bounds,
      canvasSize.width,
      canvasSize.height,
      0,
      maxZoom,
      padding ?? ReadableViewportHelper.DEFAULT_PADDING
    );
    return {
      minZoom: Math.max(
        ReadableViewportHelper.MANUAL_MIN_ZOOM,
        Math.min(minZoom, zoomToFitAll)
      ),
      maxZoom,
    };
  }

  static isOverflowing(
    bounds: Readonly<Rect>,
    canvasSize: Readonly<DiagramSize>,
    zoom: number
  ): boolean {
    return (
      bounds.width * zoom > canvasSize.width ||
      bounds.height * zoom > canvasSize.height
    );
  }

  static getViewportKeepingCentre(
    viewport: Readonly<Viewport>,
    previousSize: Readonly<DiagramSize>,
    nextSize: Readonly<DiagramSize>
  ): Viewport {
    return {
      x: viewport.x + (nextSize.width - previousSize.width) / 2,
      y: viewport.y + (nextSize.height - previousSize.height) / 2,
      zoom: viewport.zoom,
    };
  }

  static getBounds(
    nodePositions: ReadonlyArray<XYPosition>,
    nodeSize: Readonly<DiagramSize>
  ): Rect {
    const xs = nodePositions.map((p) => p.x);
    const ys = nodePositions.map((p) => p.y);
    const minX = Math.min(...xs);
    const minY = Math.min(...ys);
    return {
      x: minX,
      y: minY,
      width: Math.max(...xs) + nodeSize.width - minX,
      height: Math.max(...ys) + nodeSize.height - minY,
    };
  }

  private static getTopRankCenteredX(
    nodePositions: ReadonlyArray<XYPosition>,
    nodeSize: Readonly<DiagramSize>,
    bounds: Readonly<Rect>,
    canvasWidth: number,
    zoom: number
  ): number {
    const topY = Math.min(...nodePositions.map((p) => p.y));
    const topRankXs = nodePositions
      .filter((p) => p.y - topY < 1)
      .map((p) => p.x);
    const topRankCenter =
      (Math.min(...topRankXs) + Math.max(...topRankXs) + nodeSize.width) / 2;
    const inset = ReadableViewportHelper.OVERFLOW_INSET;
    // Clamp so neither chart edge is pulled further in than the inset.
    const maxX = inset - bounds.x * zoom;
    const minX = canvasWidth - inset - (bounds.x + bounds.width) * zoom;
    return Math.min(
      maxX,
      Math.max(minX, canvasWidth / 2 - topRankCenter * zoom)
    );
  }
}

export { ReadableViewportHelper };
